// Office call-out charge panel (Session W). Opened from a call-out row, it lets the
// office record the on-site times + travel, confirm the out-of-hours / non-contract
// basis, see a live indicative breakdown, save the authoritative Schedule of Fees
// charge (the server applies the contract tier), and raise a Xero DRAFT invoice. The
// engineer cannot edit the charge; the office is the human in the loop and the Xero
// DRAFT is the final billing step (never auto-authorised).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type { CalloutDetail, CalloutListItem } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { calculateCallOutCharge, deriveOutOfHours, pence } from "../../lib/calloutCharge";
// Session AGD: the locked call-out rate matrix, so the tier + first-hour rates shown at the top of
// this panel come from the one source (never hardcoded here).
import { estimateCallout, gbp, type CalloutTier } from "./calloutRates";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { LinkInvoiceModal } from "../../components/LinkInvoiceModal";
import { MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";

// Customer-facing tier label; ContractTier is core/care/complete (the engine's internal "cover"
// key for the middle tier never reaches this DTO).
const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };

// An ISO string -> a value for <input type="datetime-local"> (local time), or "".
function isoToLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CalloutChargeModal({ callout, onClose, onCharged }: { callout: CalloutListItem; onClose: () => void; onCharged: () => void }) {
  const [detail, setDetail] = useState<CalloutDetail | null>(null);
  const [loadError, setLoadError] = useState(false);

  const [arrival, setArrival] = useState("");
  const [departure, setDeparture] = useState("");
  const [travelHours, setTravelHours] = useState(0);
  const [nonContract, setNonContract] = useState(false);
  // null = "derive from arrival"; true/false = office override.
  const [oohOverride, setOohOverride] = useState<boolean | null>(null);
  const [chargeNotes, setChargeNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // A save that would replace the call-out's recorded charge with a different figure is refused by the server until
  // the office confirms it (storedChargeChange.ts). The server's own sentence, naming both figures, is held here and
  // the save is offered again behind a two-step confirm.
  const [chargeChange, setChargeChange] = useState<string | null>(null);
  const [invoicing, setInvoicing] = useState(false);
  const [invoiceMsg, setInvoiceMsg] = useState<string | null>(null);
  const [invoiceExists, setInvoiceExists] = useState(false);
  // Manual invoice linking (Session AO Item 3): attach this call-out to an existing Xero invoice.
  const [showPickInvoice, setShowPickInvoice] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.callouts
      .get(callout.id)
      .then((d) => {
        if (!live) return;
        setDetail(d);
        setArrival(isoToLocalInput(d.arrivalTime));
        setDeparture(isoToLocalInput(d.departureTime));
        setTravelHours(d.travelHours ?? 0);
        setNonContract(d.isNonContract);
        setChargeNotes(d.chargeNotes ?? "");
        setInvoiceExists(d.hasInvoice);
      })
      .catch(() => {
        if (live) setLoadError(true);
      });
    return () => {
      live = false;
    };
  }, [callout.id]);

  const arrivalDate = arrival ? new Date(arrival) : null;
  const departureDate = departure ? new Date(departure) : null;
  const validTimes = arrivalDate != null && departureDate != null && !Number.isNaN(arrivalDate.getTime()) && !Number.isNaN(departureDate.getTime()) && departureDate.getTime() >= arrivalDate.getTime();
  const effectiveOoh = oohOverride ?? (arrivalDate && !Number.isNaN(arrivalDate.getTime()) ? deriveOutOfHours(arrivalDate) : false);

  // Live, indicative preview at the Core rate. The server applies the contract tier
  // (Care / Complete discount) on save and returns the authoritative figures.
  const preview = useMemo(() => {
    if (!validTimes || !arrivalDate || !departureDate) return null;
    return calculateCallOutCharge({
      arrivalTime: arrivalDate,
      departureTime: departureDate,
      travelHours,
      tier: "CORE",
      isNonContract: nonContract,
      isOutOfHours: effectiveOoh,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrival, departure, travelHours, nonContract, effectiveOoh]);

  // Session AGD: the tier + first-hour rates this call-out bills at, shown at the top of the panel
  // as soon as the detail loads, so the office knows the rate before entering any time. Derived from
  // the loaded call-out's own recorded basis: the isNonContract flag wins (published double rates),
  // otherwise the lift's contract tier (defaulting to Core exactly as the server's charge does when
  // a lift has no active contract). Every figure comes from estimateCallout, never hardcoded here.
  const rateSummary = useMemo(() => {
    if (!detail) return null;
    const contract = !detail.isNonContract;
    const tier = (detail.contractTier ?? "core") as CalloutTier;
    const inHours = estimateCallout({ contract, tier, outOfHours: false, travelMins: 0 });
    const ooh = estimateCallout({ contract, tier, outOfHours: true, travelMins: 0 });
    return {
      headline: contract ? `Tier: ${TIER_LABEL[tier]}` : "Non-contract",
      bundled: inHours.bundled,
      firstHourNet: inHours.firstHourNet,
      oohFirstHourNet: ooh.firstHourNet,
    };
  }, [detail]);

  async function save(confirmChargeChange = false) {
    if (!validTimes || !arrivalDate || !departureDate) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.callouts.charge(callout.id, {
        arrivalTime: arrivalDate.toISOString(),
        departureTime: departureDate.toISOString(),
        travelHours,
        isNonContract: nonContract,
        ...(oohOverride !== null ? { isOutOfHours: oohOverride } : {}),
        chargeNotes: chargeNotes.trim() || undefined,
        ...(confirmChargeChange ? { confirmChargeChange: true } : {}),
      });
      setChargeChange(null);
      setDetail(updated);
      onCharged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === "charge_change_unconfirmed") {
        setChargeChange(typeof err.body?.message === "string" ? err.body.message : "This would replace the call-out's recorded charge with a different figure.");
        return;
      }
      setSaveError(friendlyApiError(err, "Could not save the charge. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function raiseInvoice() {
    setInvoicing(true);
    setInvoiceMsg(null);
    try {
      const res = await api.callouts.createInvoice(callout.id);
      setInvoiceMsg(res.invoiced ? "A DRAFT invoice was raised in Xero (awaiting review)." : "No invoice was raised (Xero not connected).");
      if (res.invoiced) setInvoiceExists(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setInvoiceExists(true);
        setInvoiceMsg("A Xero invoice already exists for this call-out. Reconcile the computed charge against it in Xero.");
      } else {
        setInvoiceMsg("Could not raise the invoice. Please try again.");
      }
    } finally {
      setInvoicing(false);
    }
  }

  async function linkInvoice(invoiceId: string) {
    setLinkBusy(true);
    setLinkError(null);
    try {
      const res = await api.callouts.linkInvoice(callout.id, invoiceId);
      setDetail((d) => (d ? { ...d, xeroInvoiceId: res.linkedInvoice.xeroInvoiceId, linkedInvoiceNumber: res.linkedInvoice.invoiceNumber, linkedInvoiceCacheId: res.linkedInvoice.invoiceCacheId, hasInvoice: true } : d));
      setInvoiceExists(true);
      setShowPickInvoice(false);
      onCharged(); // the row's invoice state changed
    } catch (err) {
      setLinkError(err instanceof ApiError ? err.message || err.code : "Could not link the invoice. Please try again.");
    } finally {
      setLinkBusy(false);
    }
  }

  async function unlinkInvoice() {
    setLinkBusy(true);
    setLinkError(null);
    try {
      await api.callouts.unlinkInvoice(callout.id);
      setDetail((d) => (d ? { ...d, xeroInvoiceId: null, linkedInvoiceNumber: null, linkedInvoiceCacheId: null, hasInvoice: false } : d));
      setInvoiceExists(false);
      onCharged();
    } catch {
      setLinkError("Could not unlink the invoice. Please try again.");
    } finally {
      setLinkBusy(false);
    }
  }

  const inputClass = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 outline-none focus:border-ele-green";

  return (
    <ModalShell
      ariaLabel="Call-out charge"
      onClose={() => !saving && !invoicing && !linkBusy && !showPickInvoice && onClose()}
      width="max-w-lg"
      panelClassName="max-h-[90vh] overflow-y-auto p-6"
    >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-ele-strong">Call-out charge</h2>
            <p className="text-sm text-ele-body">
              {callout.ref}
              {callout.siteName ? `, ${callout.siteName}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-1.5">
            {effectiveOoh && <StatusPill word="Out of hours" tone="red" variant="solid" />}
            {nonContract && <span className="rounded-full bg-ele-purple px-2 py-0.5 font-menu text-[10px] font-bold uppercase tracking-wide text-ele-white">Non-contract</span>}
          </div>
        </div>

        {loadError ? (
          <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-4 text-sm text-ele-strong">
            Could not load this call-out. Please close and try again.
          </p>
        ) : !detail ? (
          <Loader shape="inline" />
        ) : (
          <>
            {/* Session AGD: the tier + first-hour rates up front, so the office knows what this
                call-out bills at before entering any times. Reference info: muted, no card / border /
                accent stripe. The isNonContract flag takes precedence over any tier. */}
            {/* Session BAF (C2-017): the lift's contract was terminated for cause. Internal, so it lives on the office panel only. */}
            {detail.terminatedForCause ? (
              <p role="status" className="mb-3 text-xs font-menu uppercase tracking-wide text-[var(--ele-amber-text)]">
                {detail.terminatedForCause.text}
              </p>
            ) : null}
            {rateSummary && (
              <div className="mb-3 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ele-body">
                <span className="font-menu font-bold uppercase tracking-wide text-ele-strong">{rateSummary.headline}</span>
                <span aria-hidden="true" className="opacity-40">|</span>
                {rateSummary.bundled ? (
                  <span>Call-outs included in contract</span>
                ) : (
                  <>
                    <span>In-hours: {gbp(rateSummary.firstHourNet)} / hr</span>
                    <span aria-hidden="true" className="opacity-40">|</span>
                    <span>Out of hours: {gbp(rateSummary.oohFirstHourNet)} / hr</span>
                  </>
                )}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm" htmlFor="chg-arrival">Arrival</label>
                <input id="chg-arrival" type="datetime-local" value={arrival} onChange={(e) => setArrival(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="chg-departure">Departure</label>
                <input id="chg-departure" type="datetime-local" value={departure} onChange={(e) => setDeparture(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="chg-travel">Travel hours</label>
                <input id="chg-travel" type="number" min={0} step="0.5" value={travelHours} onChange={(e) => setTravelHours(Math.max(0, Number(e.target.value) || 0))} className={inputClass} />
              </div>
              <div className="flex flex-col justify-end gap-2">
                <label htmlFor="chg-ooh" className="flex items-center gap-2 text-sm">
                  <input id="chg-ooh" type="checkbox" checked={effectiveOoh} onChange={(e) => setOohOverride(e.target.checked)} />
                  Out of hours (doubles labour)
                </label>
                <label htmlFor="chg-nc" className="flex items-center gap-2 text-sm">
                  <input id="chg-nc" type="checkbox" checked={nonContract} onChange={(e) => setNonContract(e.target.checked)} />
                  Non-contract (doubles all rates)
                </label>
              </div>
            </div>

            {!validTimes && (arrival || departure) && (
              <p className="mt-2 text-xs text-ele-danger">Departure must be at or after arrival.</p>
            )}

            {/* Session AEW: a price agreed when the call-out was logged. Shown read-only and
                BEFORE the computed figures, because it is the thing the office needs to know
                before they compute anything: without it they would recompute the standard rate,
                get a different number, and have no way to tell it was ever agreed otherwise.
                This panel deliberately does not change the computation. Confirming a charge here
                is a later, deliberate act after the visit, and it is right that it can supersede
                a figure agreed on the phone; it just must not do so unknowingly. */}
            {detail.agreedPricePence != null && (
              <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
                <p className="font-heading text-ele-strong">
                  {detail.agreedPricePence > 0
                    ? `Agreed price at log time: ${pence(detail.agreedPricePence)} ex VAT`
                    : "Agreed free of charge at log time"}
                </p>
                {detail.agreedPriceReason && <p className="mt-1 text-xs text-ele-body">{detail.agreedPriceReason}</p>}
                {/* Session AEW review fix. This used to ASSERT that the linked draft was raised at
                    the agreed figure, but CallOut.xeroInvoiceId has several writers and only the
                    intake ones price off the agreed price: a draft raised later from this panel
                    carries the COMPUTED charge, and an invoice linked by hand carries whatever the
                    office linked. The panel cannot tell them apart, so it no longer claims to.
                    What it CAN state exactly, whatever raised the invoice, is that saving a charge
                    here never alters an existing one: PUT /:id/charge does not touch Xero, and
                    POST /:id/invoice refuses with 409 once an invoice is linked. */}
                <p className="mt-1 text-xs text-ele-body opacity-70">
                  {detail.xeroInvoiceId
                    ? "A draft invoice is already linked to this call-out. Saving a charge here records the visit but never changes that invoice, so check the figure in Xero against the agreed price before authorising it."
                    : "No invoice has been raised yet. This panel computes the standard Schedule of Fees charge from the times below, which will not match the agreed price, so raise the agreed figure in Xero by hand if that is what applies."}
                </p>
              </div>
            )}

            {preview && (
              <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
                <p className="font-heading text-ele-strong">Indicative charge (Core rate)</p>
                <div className="mt-1 flex flex-wrap justify-between gap-2">
                  <span>Labour: {pence(preview.labourCharge)}</span>
                  <span>Travel: {pence(preview.travelCharge)}</span>
                  <span className="font-heading text-ele-strong">Total: {pence(preview.totalCharge)} ex VAT</span>
                </div>
                <p className="mt-1 text-xs text-ele-body opacity-70">Save to apply this customer's rates and confirm the figure. A negotiated rate is billed as typed; the Care or Complete discount applies only to standard Schedule of Fees rates.</p>
              </div>
            )}

            <div className="mt-4">
              <label className="mb-1 block text-sm" htmlFor="chg-notes">Charge notes (optional)</label>
              <textarea id="chg-notes" rows={2} value={chargeNotes} onChange={(e) => setChargeNotes(e.target.value)} placeholder="Any agreement made on site. The Xero draft is the final billing step." className={inputClass} />
            </div>

            {detail.totalChargePence != null && (
              <div className="mt-4 rounded-ele border border-ele-green bg-ele-surface p-3 text-sm">
                <p className="font-heading text-ele-green">Saved charge</p>
                <div className="mt-1 flex flex-wrap justify-between gap-2">
                  <span>Labour: {pence(detail.labourChargePence)}</span>
                  <span>Travel: {pence(detail.travelChargePence)}</span>
                  <span className="font-heading text-ele-strong">Total: {pence(detail.totalChargePence)} ex VAT</span>
                </div>
                {detail.breakdown && <p className="mt-1 text-xs text-ele-body">{detail.breakdown}</p>}
              </div>
            )}

            {/* Invoice back-reference (Session AO Item 3): the linked Xero invoice, or "Not yet
                invoiced" with a manual link for the historical backlog. */}
            <div className="mt-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm">
              <p className="mb-1 font-heading text-ele-blue">Invoice</p>
              {detail.xeroInvoiceId ? (
                <div className="flex flex-wrap items-center gap-3">
                  {detail.linkedInvoiceCacheId ? (
                    <Link to={`/office/invoices/${encodeURIComponent(detail.linkedInvoiceCacheId)}`} className="text-ele-green underline-offset-2 hover:underline">
                      {detail.linkedInvoiceNumber ? `Invoice ${detail.linkedInvoiceNumber}` : "View invoice"}
                    </Link>
                  ) : (
                    <a
                      href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(detail.xeroInvoiceId)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ele-green underline-offset-2 hover:underline"
                    >
                      {detail.linkedInvoiceNumber ? `Invoice ${detail.linkedInvoiceNumber} (View in Xero)` : "View in Xero"}
                    </a>
                  )}
                  <button type="button" onClick={unlinkInvoice} disabled={linkBusy} className="font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-danger disabled:opacity-50">
                    {linkBusy ? "..." : "Unlink"}
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-ele-body">Not yet invoiced.</span>
                  <button type="button" onClick={() => { setLinkError(null); setShowPickInvoice(true); }} className="font-menu text-xs tracking-wide text-ele-green transition hover:underline">
                    Link to invoice
                  </button>
                </div>
              )}
              {linkError && <p role="alert" className="mt-1 text-xs text-ele-danger">{linkError}</p>}
            </div>

            {saveError && <p role="alert" className="mt-3 text-sm text-ele-danger">{saveError}</p>}
            {chargeChange && (
              <div role="alert" className="mt-3 rounded-ele bg-ele-amber-wash p-3 text-sm text-ele-amber-wash-ink">
                <p>{chargeChange}</p>
                <div className="mt-2 flex flex-wrap gap-3">
                  <ConfirmButton
                    onConfirm={() => void save(true)}
                    label="Replace the recorded charge"
                    confirmLabel="Press again to replace it"
                    busy={saving}
                    busyLabel="Saving..."
                  />
                  <button type="button" onClick={() => setChargeChange(null)} className="font-menu text-xs tracking-wide text-ele-body hover:underline">
                    Keep the recorded charge
                  </button>
                </div>
              </div>
            )}

            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button onClick={() => void save()} disabled={saving || !validTimes || chargeChange != null} className={MODAL_ACTION_BTN}>
                {saving ? "Saving..." : "Save charge"}
              </Button>
              {!invoiceExists && (
                <button
                  type="button"
                  onClick={raiseInvoice}
                  disabled={invoicing || detail.totalChargePence == null}
                  className={`rounded-ele border border-ele-blue px-4 py-2.5 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg disabled:opacity-50 ${MODAL_ACTION_BTN}`}
                >
                  {invoicing ? "Raising..." : "Create Xero DRAFT invoice"}
                </button>
              )}
              <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2.5 text-sm ${MODAL_ACTION_BTN}`}>Close</button>
            </div>
            {invoiceExists && (
              <p className="mt-2 text-xs text-ele-body opacity-70">A Xero invoice already exists for this call-out. Reconcile the computed charge against it in Xero (this avoids a duplicate DRAFT).</p>
            )}
            {invoiceMsg && <p className="mt-2 text-sm text-ele-body">{invoiceMsg}</p>}
            <LinkInvoiceModal open={showPickInvoice} busy={linkBusy} error={null} onSelect={linkInvoice} onClose={() => !linkBusy && setShowPickInvoice(false)} />
          </>
        )}
    </ModalShell>
  );
}
